import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isValidElement, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { useMeasure } from '@hivebrite/react-hooks';
import { Box } from "../../box/src";
import { __ } from "../../i18n/src";
import { Loader } from "../../loader/src";
import { Cell } from './components/Cell';
import { ColumnHeaders } from './components/ColumnHeaders';
import { EmptyState } from './components/EmptyState';
import { FadeOutLoadingBox } from './components/FadeOutLoadingBox';
import { Row } from './components/Row';
import { TablePagination } from './components/TablePagination';
import { EMPTY_CELL, MINIMAL_LOADER_HEIGHT } from './constants';
export const TableWithCursorPagination = (props) => _jsx(Table, Object.assign({}, props, { isCursorPagination: true }));
export const TableWithPagination = (props) => _jsx(Table, Object.assign({}, props));
export const Table = ({ EmptyStateComponent = EmptyState, columns = [], currentPage = 1, dataSource = [], dataTestId, handleClearSearch, hasNextPage, hasPreviousPage, isCursorPagination = false, isLoading = false, noResultText = __('No result found'), onCursorChange, onPageChange, onSort, paginationPosition = { bottom: true, top: true }, perPage = 10, totalResult = 0, }) => {
    const [tableProperties, setTableProperties] = useState({
        height: 0,
        width: 0,
    });
    const [ref, rect] = useMeasure();
    const displayNoResultState = !isLoading && totalResult === 0 && EmptyStateComponent;
    useEffect(() => {
        if (ref) {
            setTableProperties({
                height: rect.height,
                width: rect.width,
            });
        }
    }, [ref, rect.height, rect.width]);
    const loaderPosition = useMemo(() => {
        if (dataSource.length === 0) {
            return {
                height: MINIMAL_LOADER_HEIGHT,
                width: tableProperties.width / 2,
            };
        }
        return {
            height: tableProperties.height / 2,
            width: tableProperties.width / 2,
        };
    }, [dataSource, tableProperties]);
    const emptyState = useMemo(() => isValidElement(EmptyStateComponent)
        ? EmptyStateComponent
        : typeof EmptyStateComponent === 'function' && (_jsx(EmptyStateComponent, { buttonLabel: __('Clear search'), description: __('Try searching another keyword'), iconName: "megaphone", onButtonClick: handleClearSearch, title: noResultText })), [EmptyStateComponent, handleClearSearch, noResultText]);
    return (_jsxs(TableWrapper, { "data-testid": dataTestId, ref: ref, children: [isLoading && (_jsx(LoaderWrapper, { "$loaderPosition": loaderPosition, color: "primary", children: _jsx(Loader, { size: 48, strokeWidth: 4 }) })), _jsx(TablePagination, { currentPage: currentPage, hasNextPage: hasNextPage, hasPagination: !displayNoResultState && totalResult > perPage, hasPreviousPage: hasPreviousPage, isCursorPagination: isCursorPagination, isLoading: isLoading, onCursorChange: onCursorChange, onPageChange: onPageChange, paginationPosition: paginationPosition, perPage: perPage, totalResult: totalResult, children: _jsxs(_Fragment, { children: [_jsxs(StyledTable, { as: "table", children: [_jsx(ColumnHeaders, { columns: columns, onSort: onSort }), _jsx(Body, { as: "tbody", children: dataSource.map((row) => (_jsx(Row, { "$isLoading": isLoading, as: "tr", "data-testid": (row === null || row === void 0 ? void 0 : row.dataTestId) || 'ds-table-body-row', display: "table-row", children: columns.map((column) => {
                                            var _a;
                                            return (_jsx(Cell, { "$verticalAlign": "middle", as: "td", "data-testid": "ds-table-body-cell", font: "body-regular-asphalt", textAlign: column.textAlign || 'left', width: column.width, children: ((_a = column.renderCell) === null || _a === void 0 ? void 0 : _a.call(column, {
                                                    data: row,
                                                })) ||
                                                    (column.dataIndex && row[column.dataIndex]) ||
                                                    EMPTY_CELL }, column.key));
                                        }) }, row.key))) })] }), displayNoResultState && (_jsx(FadeOutLoadingBox, { "$isLoading": isLoading, flexDirection: "column", children: emptyState }))] }) })] }));
};
const LoaderWrapper = styled(Box).attrs(() => ({
    display: 'block',
})) `
  position: absolute;
  transform: translate(
    ${({ $loaderPosition }) => $loaderPosition.width}px,
    ${({ $loaderPosition }) => $loaderPosition.height}px
  );
`;
const StyledTable = styled(Box).attrs(() => ({
    display: 'table',
    flexDirection: 'column',
    width: '100%',
})) `
  border-collapse: collapse;
`;
const Body = styled(Box).attrs(() => ({
    display: 'table-row-group',
    flexDirection: 'column',
    width: '100%',
})) ``;
const TableWrapper = styled(Box).attrs(() => ({
    display: 'block',
    width: '100%',
})) ``;
//# sourceMappingURL=Table.js.map